<template>
  <div
    class="ai-visibility-question-sidebar-metrics"
    :style="gridStyle"
    aria-label="Question rank summary"
  >
    <article
      v-for="metric in metrics"
      :key="metric.label"
      class="ai-visibility-question-sidebar-metrics__card"
      :class="metric.tone ? `ai-visibility-question-sidebar-metrics__card--${metric.tone}` : ''"
    >
      <span>{{ metric.label }}</span>
      <strong>{{ metric.value }}</strong>
    </article>
  </div>
</template>

<script>
export default {
  name: 'QuestionSidebarMetricCards',
  props: {
    question: {
      type: Object,
      required: true,
    },
  },
  computed: {
    metrics () {
      return [
        {
          label: 'You',
          value: this.question.statusLabel,
          tone: this.question.status,
        },
        {
          label: 'Your rank',
          value: this.rankValue,
          tone: '',
        },
        {
          label: 'Vs. last week',
          value: this.lastRunLabel,
          tone: this.question.trend?.tone || '',
        },
      ].filter(metric => metric.value !== null)
    },
    rankValue () {
      if (this.question.status === 'unknown') {
        return '-'
      }

      if (this.question.status === 'missing') {
        return `- of ${this.question.mentionedBrandCount || 0}`
      }

      if (!this.question.mentionedBrandCount) {
        return this.question.rankLabel
      }

      return `${this.question.rankLabel} of ${this.question.mentionedBrandCount}`
    },
    lastRunLabel () {
      const trend = this.question.trend || {}
      if (this.question.trend === null) {
        return null
      }

      if (!trend.value) {
        return 'No change'
      }

      const absoluteValue = this.formatAbsoluteValue(trend.value)
      if (trend.value < 0) {
        return `Improved ${absoluteValue}`
      }

      return `Dropped ${absoluteValue}`
    },
    gridStyle () {
      return {
        gridTemplateColumns: `repeat(${this.metrics.length}, minmax(0, 1fr))`,
      }
    },
  },
  methods: {
    formatAbsoluteValue (value) {
      const absoluteValue = Math.abs(Number(value || 0))
      if (Number.isInteger(absoluteValue)) {
        return String(absoluteValue)
      }

      return absoluteValue.toFixed(1)
    },
  },
}
</script>

<style lang="scss" scoped>

.ai-visibility-question-sidebar-metrics {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 14px;
}

.ai-visibility-question-sidebar-metrics__card {
  display: flex;
  min-height: 78px;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  border: 1px solid var(--v-gray12Color-base);
  border-radius: 12px;
  background: var(--v-cardBg-base);
  padding: 12px;
  text-align: center;
  box-shadow: 0 5px 2px 0 rgba(0, 0, 0, 0.01), 0 3px 2px 0 rgba(0, 0, 0, 0.04), 0 1px 1px 0 rgba(0, 0, 0, 0.08), 0 0 1px 0 rgba(0, 0, 0, 0.08);
}

.ai-visibility-question-sidebar-metrics__card span {
  color: #363636;
  font-size: 16px;
  font-weight: 400;
}

.ai-visibility-question-sidebar-metrics__card strong {
  color: var(--v-mainColor-base);
  font-size: 16px;
  line-height: 1.2;
}

.ai-visibility-question-sidebar-metrics__card--missing strong,
.ai-visibility-question-sidebar-metrics__card--slipping strong,
.ai-visibility-question-sidebar-metrics__card--negative strong {
  color: var(--v-red11Color-base);
}

.ai-visibility-question-sidebar-metrics__card--winning strong,
.ai-visibility-question-sidebar-metrics__card--positive strong {
  color: var(--v-green8Color-base);
}

.ai-visibility-question-sidebar-metrics__card--holding strong {
  color: var(--v-link4Color-base);
}

.ai-visibility-question-sidebar-metrics__card--unknown strong,
.ai-visibility-question-sidebar-metrics__card--neutral strong {
  color: var(--v-gray50Color-base);
}

@media (max-width: 420px) {
  .ai-visibility-question-sidebar-metrics {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
